import React from 'react'
import { Plus, AlertCircle } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Session, Room } from '@/types'
import MobileSessionCell from './MobileSessionCell'
import { useLongPress } from '@/hooks/useLongPress'

// Status border colors (left accent)
const STATUS_BORDER_COLORS: Record<string, string> = {
  not_started: 'border-l-gray-300',
  past_not_created: 'border-l-red-400',
  past_deadline: 'border-l-red-500',
  incomplete_sections: 'border-l-blue-400',
  awaiting_validation: 'border-l-amber-400',
  complete: 'border-l-emerald-400',
  report_generated: 'border-l-purple-400',
}

interface SectionSummary {
  section_id: number | null
  section_name: string
  image_count: number
  detection_count: number
  status: 'no_upload' | 'not_validated' | 'validated'
}

interface SessionSummary {
  session_id: number
  sections: SectionSummary[]
  total_detections: number
  overall_status: 'not_started' | 'incomplete_sections' | 'awaiting_validation' | 'complete'
}

interface MobileRoomCardProps {
  room: Room
  session: Session | undefined
  summary: SessionSummary | undefined
  date: Date
  isPast: boolean
  isFuture: boolean
  isSunday: boolean
  isSelected?: boolean
  selectionMode?: boolean
  onSessionClick?: () => void
  onCreateSession?: () => void
  onToggleSelection?: () => void
  onSessionLongPress?: (session: Session) => void
}

/**
 * A card representing a room for a specific day on mobile
 */
const MobileRoomCard: React.FC<MobileRoomCardProps> = ({
  room,
  session,
  summary,
  isPast,
  isFuture,
  isSunday,
  isSelected,
  selectionMode,
  onSessionClick,
  onCreateSession,
  onSessionLongPress,
}) => {
  // Determine status key for border color
  const getStatusKey = (): string => {
    if (!session) {
      if (isPast && !isSunday) return 'past_not_created'
      return 'not_started'
    }
    return summary?.overall_status || 'not_started'
  }

  const statusKey = getStatusKey()
  const isPastNotCreated = !session && isPast && !isSunday
  const canCreate = !session && !isSunday && !isFuture && !isPast
  const canOpen = !!session && !isSunday

  const handleClick = () => {
    if (canOpen && onSessionClick) {
      onSessionClick()
    } else if (canCreate && onCreateSession) {
      onCreateSession()
    }
  }

  const handleLongPress = () => {
    if (session && onSessionLongPress) {
      // Provide haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50)
      }
      onSessionLongPress(session)
    }
  }

  // Set up long press handler
  const longPressHandlers = useLongPress({
    onLongPress: handleLongPress,
    onClick: handleClick,
    delay: 500,
  })

  return (
    <div
      className={cn(
        'bg-white rounded-xl shadow-sm border overflow-hidden',
        'transition-all duration-200',
        'select-none', // Prevent text selection during long press
        session && `border-l-4 ${STATUS_BORDER_COLORS[statusKey] || 'border-l-gray-300'}`,
        !session && 'border-l-4 border-l-transparent',
        isSelected && 'ring-2 ring-primary-500 bg-primary-50/50',
        selectionMode && !session && 'opacity-50',
        (canOpen || canCreate) && 'active:scale-[0.98]',
        isSunday && 'opacity-60 bg-gray-50',
        isFuture && 'opacity-60 bg-gray-50',
        isPastNotCreated && 'bg-red-50/50 border-red-200'
      )}
      {...longPressHandlers}
    >
      {/* Room Header */}
      <div
        className={cn(
          'px-4 py-3 border-b',
          session ? 'bg-gray-50/50' : 'bg-gray-50'
        )}
      >
        <h3 className="font-medium text-gray-900">{room.name}</h3>
      </div>

      {/* Card Content */}
      <div className="p-4 min-h-[80px]">
        {/* Session exists */}
        {session && !isSunday && (
          <MobileSessionCell
            summary={summary}
            isRetrospective={session.is_retrospective}
          />
        )}

        {/* Sunday - closed */}
        {isSunday && (
          <div className="flex items-center justify-center h-12 text-gray-400">
            <span className="text-sm">Closed on Sunday</span>
          </div>
        )}

        {/* Future date */}
        {!session && isFuture && !isSunday && (
          <div className="flex items-center justify-center h-12 text-gray-400">
            <span className="text-sm">Upcoming</span>
          </div>
        )}

        {/* Past - not created (missed) */}
        {isPastNotCreated && (
          <div className="flex items-center justify-center h-12 gap-2 text-red-500">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Missed</span>
          </div>
        )}

        {/* Today - can create */}
        {canCreate && (
          <div className="flex items-center justify-center h-12">
            <div className="flex items-center gap-2 text-primary-600">
              <div className="w-10 h-10 rounded-full border-2 border-dashed border-primary-300 flex items-center justify-center">
                <Plus className="h-5 w-5" />
              </div>
              <span className="text-sm font-medium">Create Session</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MobileRoomCard
